import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

const Reveal = ({ children, animationType }) => {
  const ref = useRef(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  // Check if IntersectionObserver is supported
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: show immediately if IntersectionObserver not supported
      setShouldAnimate(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  // Use framer-motion hooks (will work in modern browsers)
  const isInView = useInView(ref, { once: true });
  const mainControls = useAnimation();

  useEffect(() => {
    if (isInView && shouldAnimate) {
      mainControls.start('visible');
    }
  }, [isInView, shouldAnimate, mainControls]);

  const getAnimationVariants = () => {
    switch (animationType) {
      case 'fadeInUp':
        return { opacity: 0, y: 75 };
      case 'fadeInDown':
        return { opacity: 0, y: -75 };
      case 'fadeInRight':
        return { opacity: 0, x: -75 };
      case 'fadeInLeft':
        return { opacity: 0, x: 75 };
      default:
        return { opacity: 0, y: 75 };
    }
  };

  const animationVariants = getAnimationVariants();

  // Fallback for browsers without framer-motion support
  if (!shouldAnimate) {
    return (
      <div ref={ref} style={{ position: 'relative', overflow: 'hidden', opacity: 0 }}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', overflow: 'hidden' }}>
      <motion.div
        variants={{
          hidden: animationVariants,
          visible: { opacity: 1, y: 0, x: 0 },
        }}
        initial="hidden"
        animate={mainControls}
        transition={{ duration: 1, delay: 0.25 }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default Reveal;
